import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { SideNavExpanded } from "../styles/SideNav.styles";
import PanelControlComponent from "./PanelControl";
import PaneEdgeHandle from "./PaneEdgeHandle";
import Box from "../../Box";

/**
 * ExpandedPanel - A resizable panel component for expanded navigation items
 *
 * Renders an expandable panel that can be resized by the user. Supports both
 * desktop (horizontal resize) and mobile (vertical resize) orientations.
 * Visibility is controlled by the parent (expandedItem index match).
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - Navigation item data
 * @param {number} [props.expandedItem] - Currently expanded item index
 * @param {boolean} props.isExpanded - Whether the navigation is expanded
 * @param {number} [props.expandedWidth] - Width of the expanded panel (desktop)
 * @param {boolean} props.isSmallScreen - Whether currently on a small screen
 * @param {React.RefObject} props.expandedRef - Ref for the expanded panel
 * @param {Function} props.onResizeStart - Resize start handler
 * @param {Function} props.onItemClick - Item click handler
 * @param {boolean} props.isResizing - Whether a resize operation is currently active
 * @param {boolean} props.hasResized - Whether any resize movement has occurred
 * @returns {JSX.Element} Rendered expanded panel or null if not applicable
 */
const ExpandedPanel = ({
  item,
  expandedItem,
  isExpanded,
  expandedWidth,
  isSmallScreen,
  expandedRef,
  onResizeStart,
  onItemClick,
  isResizing,
  hasResized,
}) => {
  const panelRef = useRef(null);
  const isHoveringInPanelRef = useRef(false);

  // Handle hover behavior for the panel
  useEffect(() => {
    if (!panelRef.current) return;

    const panel = panelRef.current;

    const handleMouseEnter = () => {
      isHoveringInPanelRef.current = true;
    };

    const handleMouseLeave = () => {
      isHoveringInPanelRef.current = false;
    };

    panel.addEventListener("mouseenter", handleMouseEnter);
    panel.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      panel.removeEventListener("mouseenter", handleMouseEnter);
      panel.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, []);

  if (item.actionType !== "component" || item.hide) {
    return null;
  }

  return (
    <Box
      position="relative"
      height="100%"
      zIndex={isSmallScreen ? "1000" : "1"}
    >
      <SideNavExpanded
        ref={(el) => {
          expandedRef.current = el;
          panelRef.current = el;
        }}
        tabIndex="0"
        active={expandedItem}
        large={item.large}
        isExpanded={isExpanded}
        $isSmallScreen={isSmallScreen}
        width={!isSmallScreen ? expandedWidth : undefined}
        height={isSmallScreen ? expandedWidth : undefined}
      >
        <PanelControlComponent onClick={() => onItemClick(item, true)} />
        {item.component()}
      </SideNavExpanded>
      <PaneEdgeHandle
        edge="end"
        isSmallScreen={isSmallScreen}
        onMouseDown={onResizeStart}
        onClick={(e) => {
          // Prevent click from firing if we're currently resizing or if we've resized
          if (isResizing || hasResized) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          onItemClick(item, true);
        }}
      />
    </Box>
  );
};

ExpandedPanel.propTypes = {
  item: PropTypes.shape({
    actionType: PropTypes.string.isRequired,
    hide: PropTypes.bool,
    large: PropTypes.bool,
    component: PropTypes.elementType.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  expandedItem: PropTypes.number,
  isExpanded: PropTypes.bool,
  expandedWidth: PropTypes.number,
  isSmallScreen: PropTypes.bool,
  expandedRef: PropTypes.object,
  onResizeStart: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  isResizing: PropTypes.bool,
  hasResized: PropTypes.bool,
};

export default ExpandedPanel;
